import { useQuery } from "@tanstack/react-query";

import { adminCustomersWishlist } from "@motolynk/api-client";
import { formatDateTime, useUiLabels, type CustomerTabProps } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import {
  Badge,
  Card,
  CardContent,
  EmptyState,
  ErrorState,
  formatMoney,
  LoadingState,
} from "@motolynk/ui";

/** The admin customer page's "Wishlist" tab: what the customer saved, with today's price and stock (read-only). */
export function CustomerWishlistTab({ customerUid }: CustomerTabProps) {
  const { t, i18n } = useTranslation("wishlist");
  const ui = useUiLabels();
  const rows = useQuery({
    queryKey: ["admin", "customers", customerUid, "wishlist"],
    queryFn: async () => (await adminCustomersWishlist(customerUid)).data,
  });
  if (rows.isPending) return <LoadingState />;
  if (rows.isError) {
    return (
      <ErrorState
        title={t("customerTab.loadFailed")}
        retryLabel={ui.retry}
        onRetry={() => void rows.refetch()}
      />
    );
  }
  if (rows.data.length === 0) return <EmptyState icon="heart" title={t("customerTab.empty")} />;
  return (
    <ul className="grid gap-4 md:grid-cols-2">
      {rows.data.map((row) => (
        <li key={row.uid}>
          <Card>
            <CardContent className="space-y-1 pt-6 text-sm">
              <p className="font-medium">{row.product.title}</p>
              <p className="text-muted-foreground">
                {t("customerTab.sku", { sku: row.variant.sku, store: row.product.vendor.name })}
              </p>
              <p className="flex flex-wrap items-center gap-2">
                {formatMoney(row.variant.price)}
                {Number(row.price_drop) > 0 && (
                  <Badge tone="success">
                    {t("customerTab.drop", { amount: formatMoney(row.price_drop) })}
                  </Badge>
                )}
                {!row.available ? (
                  <Badge tone="danger">{t("customerTab.unavailable")}</Badge>
                ) : (
                  !row.in_stock && <Badge tone="warning">{t("customerTab.outOfStock")}</Badge>
                )}
              </p>
              <p className="text-muted-foreground">
                {t("customerTab.added", { date: formatDateTime(row.added_at, i18n.language) })}
              </p>
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
}
